import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import Modal from 'components/Modal';
import { Form, Input, Spin } from 'antd';
import CurrencyInputPrecision from 'components/CurrencyInputPrecision';
import styled from 'styled-components';
import toast from 'utils/toast';
import settingApis from 'features/settingService/services/apis';
import { ICreateOrUpdateServiceBodyType } from 'features/settingService/services/types/api';
import { IResponseDataBody } from 'services/response';
import get from 'lodash/get';
import { IServiceItemData } from 'services/shop/types/categories';

type IFormData = {
    serviceName: string;
    sellPrice: number;
}
type ModalAddNewServiceRef = {
    open: () => Promise<IServiceItemData | null>;
}

export const useModalAddNewServicesRef = () => useRef<ModalAddNewServiceRef>(null);
const ModalAddNewServices = forwardRef<ModalAddNewServiceRef>((_, ref) => {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const callBackRef = useRef<((data: IServiceItemData | null) => void) | null>(null);
    const [form] = Form.useForm<IFormData>();

    const handleClose = () => {
        setVisible(false);
        form.resetFields();
    };

    const handleOpen = () => {
        setVisible(true);

    };

    useImperativeHandle(ref, () => ({
        open: async () => {
            handleOpen();
            return new Promise<IServiceItemData | null>((resolve) => {
                callBackRef.current = resolve;
            });
        }
    }));

    const onFinish = async (values: IFormData) => {
        try {
            setLoading(true);
            const body: ICreateOrUpdateServiceBodyType = {
                categoryId: null,
                serviceId: null,
                serviceName: values.serviceName,
                priceSell: values.sellPrice,
                orderNumber: 0,
                duration: 0,
                isAddOn: true,
                supplyFee: 0,
                description: '',
                urlVideo: '',
                childrenIds: [],
            };
            const res: IResponseDataBody<IServiceItemData> = await settingApis.createOrUpdateService(body);
            if (res.data.data) {
                toast.success('Service added successfully');
                handleClose();
                callBackRef.current?.(res.data.data);
            } else {
                toast.error('Failed to add new service');
                callBackRef.current?.(null);
            }
        } catch (error) {
            const msg = get(error, 'response.data.message');
            toast.error(msg || 'Failed to add new service');
            callBackRef.current?.(null);
        } finally {
            setLoading(false);
        }
    };

    if (!visible) return null;
    return (
        <Modal
            modalTitle={'Add New Service'}
            visible={visible}
            onClose={handleClose}
            onSubmit={() => form.submit()}
            width={'900px'}
            noneBodyStyle={true}
            noneFooterStyle={true}
            footerPadding={0.5}
            okTitle='OK'
            v2={true}
        >
            <Spin spinning={loading}>
                <Container>
                    <Form layout='vertical' form={form} onFinish={onFinish}>
                        <Form.Item name='serviceName' label='Service Name' rules={[{ required: true, message: 'Please enter service name' }]}>
                            <Input />
                        </Form.Item>
                        <Form.Item name='sellPrice' label='Sell Price' rules={[{ required: true, message: 'Please enter sell price' }]}>
                            <CurrencyInputPrecision />
                        </Form.Item>
                    </Form>
                </Container>
            </Spin>
        </Modal>
    );
});

ModalAddNewServices.displayName = 'ModalAddNewServices';
export default ModalAddNewServices;


const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px 16px;
`;